import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import useDeliveryNoteService from '../../hooks/useDeliveryNoteService';
import useInvoiceService from '../../hooks/useInvoiceService';
import useDocumentService from '../../hooks/useDocumentService';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
    CircularProgress,
    Typography,
    Button,
    Card,
    CardContent,
    CardHeader,
    TableCell,
    TableRow,
    TableHead,
    TableBody,
    TextField,
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    TableContainer,
    Table,
    Paper,
    Alert,
} from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import UploadDocument from '../../components/UploadDocument';
import { formatDate, formatPrice, DatePickerComponent, handleApiError } from '../../components/Utils';

dayjs.extend(utc);

function EditDeliveryNote() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const { getDeliveryNote } = useDeliveryNoteService();
    const { createInvoice } = useInvoiceService();
    const { getDocumentBlob } = useDocumentService();
    const showSnackbar = useSnackbar();
    const [error, setError] = useState(null);

    const [openInvoice, setOpenInvoice] = useState(false);
    const [retention, setRetention] = useState(null);
    const [retentionDate, setRetentionDate] = useState(null);
    const [invoiceLimitDate, setInvoiceLimitDate] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(dayjs().startOf('day').local().format());
    const [vat, setVat] = useState(null);
    const [externalProviderId, setExternalProviderId] = useState(null);

    const [deliveryNoteData, setDeliveryNoteData] = useState(null);

    const [retentionError, setRetentionError] = useState('');
    const [vatError, setVatError] = useState('');
    const [externalProviderIdError, setExternalProviderIdError] = useState(null);

    const fetchDeliveryNote = useCallback(async () => {
        try {
            const result = await getDeliveryNote(id);
            setDeliveryNoteData(result.data);
            setLoading(false);
        } catch (error) {
            setError("Error obteniendo albarán");
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchDeliveryNote();
    }, []);

    const handleCreateInvoice = async () => {
        try {
            await createInvoice({
                delivery_notes: [id],
                retention: retention,
                vat: vat,
                invoice_limit_date: invoiceLimitDate,
                retention_limit_date: retentionDate,
                invoice_date: invoiceDate,
                external_provider_id: externalProviderId
            });
            showSnackbar('Factura creada correctamente', 'success');
            fetchDeliveryNote();
            handleCloseInvoice(false);
            setError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al crear la factura.');
            setError(msg);
            showSnackbar(msg, 'error');
        }
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError(handleApiError(error, 'Error al descargar el documento.'));
        }
    };

    const handleDateChange = (name, newValue) => {
        const date = dayjs(newValue).hour(12).minute(0).second(0).utc().format('YYYY-MM-DD');
        if (name === 'retention_limit_date') {
            setRetentionDate(date);
        } else if (name === 'invoice_date') {
            setInvoiceDate(date);
        } else if (name === 'invoice_limit_date') {
            setInvoiceLimitDate(date);
        }
    };

    const handleChangeRetention = (event) => {
        const { value } = event.target;

        setRetention(value);

        const retentionValue = parseFloat(value);

        if (isNaN(retentionValue) || retentionValue < 0 || retentionValue > 100 || !Number.isInteger(retentionValue)) {
            setRetentionError('La retención debe ser un número entero entre 0 y 100');
        } else {
            setRetentionError('');
        }
    };

    const handleChangeExternalId = (event) => {
        const { value } = event.target;

        setExternalProviderId(value);

        if (value.length > 50) {
            setExternalProviderIdError('El valor no debe exceder los 50 caracteres');
        } else {
            setExternalProviderIdError('');
        }
    };

    const handleChangeVat = (event) => {
        const { value } = event.target;

        setVat(value);

        const vatValue = parseFloat(value);

        if (isNaN(vatValue) || vatValue < 0 || vatValue > 100 || !Number.isInteger(vatValue)) {
            setVatError('El IVA debe ser un número entero entre 0 y 100');
        } else {
            setVatError('');
        }
    };

    const handleCloseInvoice = () => {
        setRetention(null);
        setRetentionDate(null);
        setInvoiceLimitDate(null);
        setOpenInvoice(false);
    };

    const handleClickOpenInvoice = () => {
        setOpenInvoice(true);
    };

    const invoiceData = deliveryNoteData?.invoices?.[0];
    const invoiceBase = invoiceData ? invoiceData.base_price || 0 : 0;
    const retentionAmount = invoiceData ? invoiceData.retention || 0 : 0;
    const total = (invoiceData?.total_price || 0) - retentionAmount;
    const vatAmount = (invoiceData?.total_price || 0) - invoiceBase;

    const isButtonDisabled = retentionError || externalProviderIdError || vatError || !retention || !retentionDate || !vat || !invoiceLimitDate;

    const deliveryNoteTotal = deliveryNoteData?.delivery_note_product_orders.reduce((acc, order) => {
        const totalPrice = order.amount * order.product_order.price;
        return acc + totalPrice;
    }, 0);

    return (
        <div style={{ padding: 20 }}>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            ) : (
                <div>
                    {/* Mostrar el mensaje de error si existe */}
                    {error && (
                        <Alert severity="error" style={{ marginBottom: '20px' }}>
                            {error}
                        </Alert>
                    )}

                    <Card sx={{ margin: 'auto' }}>
                        <CardHeader
                            title={
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', padding: '20px 0' }}>
                                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                                        {`Albarán: ${deliveryNoteData.code} | Fecha: ${formatDate(deliveryNoteData.dn_date) || '-'}`}
                                    </span>
                                </div>
                            }
                            sx={{
                                backgroundColor: '#f5f5f5',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                padding: '16px 0'
                            }}
                        />
                        <CardContent>
                            <Grid container spacing={3}>
                                {/* Información del Proveedor */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={3} sx={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', marginBottom: '20px', textAlign: 'left' }}>
                                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', fontSize: '16px' }}>
                                            Información del Proveedor
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontSize: '14px', marginBottom: '10px' }}>
                                            <strong>Código: </strong>
                                            <Link to={`/providers/update/${deliveryNoteData.provider.id}`}>
                                                {deliveryNoteData.provider.code || '-'} <br />
                                            </Link>
                                            <strong>Nombre: </strong> {deliveryNoteData.provider.name || '-'} <br />
                                            <strong>Email: </strong> {deliveryNoteData.provider.email || '-'} <br />
                                            <strong>Teléfono: </strong> {deliveryNoteData.provider.telephone_number || '-'} <br />
                                            <strong>Documentación: </strong> {deliveryNoteData.provider.documentation || '-'} <br />
                                        </Typography>
                                    </Paper>
                                </Grid>

                                {/* Información de la Factura */}
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={3} sx={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', marginBottom: '20px', textAlign: 'left' }}>
                                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', fontSize: '16px' }}>
                                            Información de la Factura
                                        </Typography>
                                        {invoiceData ? (
                                            <Typography variant="body2" sx={{ fontSize: '14px', marginBottom: '10px' }}>
                                                <strong>Código: </strong>
                                                <Link to={`/invoices/update/${invoiceData.id}`}>
                                                    {invoiceData.code} <br />
                                                </Link>
                                                <strong>Código externo: </strong> {invoiceData.external_provider_id || '-'} <br />
                                                <strong>Fecha de la Factura: </strong> {formatDate(invoiceData.invoice_date) || '-'} <br />
                                                <strong>Base imponible: </strong> {formatPrice(invoiceBase)} <br />
                                                <strong>IVA: </strong> {invoiceData.vat}% ({formatPrice(vatAmount)}) <br />
                                                <strong>Retención: </strong> {formatPrice(retentionAmount)} <br />
                                                <strong>Total: </strong> {formatPrice(total)} <br />
                                            </Typography>
                                        ) : (
                                            <Button variant="contained" color="primary" style={{ marginTop: 20 }} onClick={handleClickOpenInvoice}>
                                                Facturar
                                            </Button>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Tabla de productos del Albarán */}
                            <Paper elevation={3} sx={{ padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px', marginTop: '20px' }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: '16px', textAlign: 'center' }}>
                                    Obra:
                                    <Link to={`/buildings/update/${deliveryNoteData.building.id}`}>
                                        {deliveryNoteData.building.code || '-'}
                                    </Link> - {deliveryNoteData.building.name || '-'}
                                </Typography>

                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Producto</strong></TableCell>
                                                <TableCell><strong>Cantidad</strong></TableCell>
                                                <TableCell><strong>Precio Unitario</strong></TableCell>
                                                <TableCell><strong>Total</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {deliveryNoteData.delivery_note_product_orders.map((order) => {
                                                const product = order.product_order;
                                                const totalPrice = order.amount * product.price;

                                                return (
                                                    <TableRow key={order.id}>
                                                        <TableCell>{product.product_name || '-'}</TableCell>
                                                        <TableCell>{order.amount} {product.unit}</TableCell>
                                                        <TableCell>{product.price.toFixed(2)} €</TableCell>
                                                        <TableCell>{totalPrice.toFixed(2)} €</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {/* Información adicional del total */}
                                            <TableRow>
                                                <TableCell rowSpan={3} colSpan={2}></TableCell>
                                                <TableCell><strong>Base imponible</strong></TableCell>
                                                <TableCell>{deliveryNoteTotal.toFixed(2)} €</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell><strong>Total</strong></TableCell>
                                                <TableCell><strong>{deliveryNoteTotal.toFixed(2)} €</strong></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Espacio y alineación de UploadDocument */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                {deliveryNoteData.documents && deliveryNoteData.documents.length > 0 && (
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            handleDownloadDocument(deliveryNoteData.document[0].id, deliveryNoteData.document[0].name)
                                        }
                                        sx={{ marginRight: 2 }}
                                    >
                                        {deliveryNoteData.documents[0].name}
                                    </Button>
                                )}
                                <UploadDocument
                                    to={'delivery_note_id'}
                                    id={id}
                                    fetch={fetchDeliveryNote}
                                    showDisableDoc={deliveryNoteData.documents && deliveryNoteData.documents.length}
                                >
                                </UploadDocument>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Diálogo de creación de factura */}
                    <Dialog open={openInvoice} onClose={handleCloseInvoice}>
                        <DialogTitle>Crear Factura</DialogTitle>
                        <DialogContent>
                            <br />
                            <Grid>
                                <DatePickerComponent
                                    label="Fecha factura"
                                    value={invoiceDate}
                                    onChange={(newValue) => handleDateChange('invoice_date', newValue)}
                                />
                            </Grid>
                            <br />
                            <Grid>
                                <DatePickerComponent
                                    label="Fecha vencimiento factura"
                                    value={invoiceLimitDate}
                                    onChange={(newValue) => handleDateChange('invoice_limit_date', newValue)}
                                />
                            </Grid>
                            <Grid>
                                <TextField
                                    name="vat"
                                    label="IVA (%)"
                                    fullWidth
                                    value={vat}
                                    onChange={handleChangeVat}
                                    margin="normal"
                                    type="number"
                                    error={!!vatError}
                                    helperText={vatError}

                                />
                            </Grid>
                            <Grid>
                                <TextField
                                    name="retention"
                                    label="Retención (%)"
                                    fullWidth
                                    value={retention}
                                    onChange={handleChangeRetention}
                                    margin="normal"
                                    type="number"
                                    error={!!retentionError}
                                    helperText={retentionError}
                                />
                            </Grid>
                            <br />
                            <Grid>
                                <DatePickerComponent
                                    label="Fecha vencimiento retención"
                                    value={retentionDate}
                                    onChange={(newValue) => handleDateChange('retention_limit_date', newValue)}
                                />
                            </Grid>
                            <Grid>
                                <TextField
                                    fullWidth
                                    label="Id externo"
                                    name="external_provider_id"
                                    value={externalProviderId}
                                    onChange={handleChangeExternalId}
                                    margin="normal"
                                    inputProps={{ maxLength: 50 }}
                                    error={!!externalProviderIdError}
                                    helperText={externalProviderIdError}
                                />
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseInvoice} color="secondary">
                                Cancelar
                            </Button>
                            <Button onClick={handleCreateInvoice} disabled={isButtonDisabled} color="primary">
                                Crear factura
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
            )}
        </div>
    );
}

export default EditDeliveryNote;
